import { DEFAULT_PROPERTIES } from '../data/defaultProperties';
import { supabase } from '../supabaseClient';

const CACHE_KEY = 'casas_ernestina_propiedades_cache';

const isBrowser = typeof window !== 'undefined';
const isLocal = isBrowser && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const API_URL = isLocal
  ? 'http://localhost:5000/api'
  : (process.env.REACT_APP_API_URL || 'https://casas-api.onrender.com/api');

const TURSO_DB_URL = process.env.REACT_APP_TURSO_DATABASE_URL || 'https://casasernestina-andriyvil.aws-us-east-1.turso.io';
const TURSO_HTTP_URL = TURSO_DB_URL.replace('libsql://', 'https://') + (TURSO_DB_URL.endsWith('/v2/pipeline') ? '' : '/v2/pipeline');
const TURSO_AUTH_TOKEN = process.env.REACT_APP_TURSO_AUTH_TOKEN || 'eyJhbGciOiJFZERTQSIsInR5cCI6IkpXVCJ9.eyJhIjoicnciLCJpYXQiOjE3NzEyMTAyNjQsImlkIjoiNWQ2NDg1MzMtNzcxYy00Yzk3LTg4NDQtZTEwNzczZTA5ZGJjIiwicmlkIjoiNjJhOTc0ZjQtZGFjZi00MzBlLWJlMGYtNzg5NzFhZWVjN2MyIn0.COnv7ttvHViV-8Ow7-rQEXhv_jdHtM2ojtYnwcVZ2FugEItF1X5GT_LTnXxC9I25KsXXi_x8yUYxEySUXLT8DQ';

/**
 * Limpia y formatea una propiedad individual de forma segura.
 */
export function sanitizeProperty(p) {
  if (!p || typeof p !== 'object') return null;

  // Galería segura
  let galeria = [];
  if (Array.isArray(p.galeria)) {
    galeria = p.galeria;
  } else if (typeof p.galeria === 'string' && p.galeria.trim()) {
    try {
      const parsed = JSON.parse(p.galeria);
      if (Array.isArray(parsed)) galeria = parsed;
    } catch {
      galeria = p.galeria.split(',').map(s => s.trim().replace(/["[\]]/g, '')).filter(Boolean);
    }
  }

  // Amenidades seguras y limpias (sin corchetes ni comillas residuales)
  let amenidades = [];
  if (Array.isArray(p.amenidades)) {
    amenidades = p.amenidades
      .map(a => (typeof a === 'string' ? a.trim().replace(/["[\]]/g, '') : String(a || '')))
      .filter(Boolean);
  } else if (typeof p.amenidades === 'string' && p.amenidades.trim()) {
    try {
      const parsed = JSON.parse(p.amenidades);
      if (Array.isArray(parsed)) {
        amenidades = parsed
          .map(a => (typeof a === 'string' ? a.trim().replace(/["[\]]/g, '') : String(a || '')))
          .filter(Boolean);
      } else {
        amenidades = p.amenidades.split(',').map(a => a.trim().replace(/["[\]]/g, '')).filter(Boolean);
      }
    } catch {
      amenidades = p.amenidades.split(',').map(a => a.trim().replace(/["[\]]/g, '')).filter(Boolean);
    }
  }

  return {
    id: p.id,
    titulo: p.titulo || 'Casa Vacacional',
    categoria: p.categoria || 'Playa',
    precio: p.precio !== undefined && p.precio !== null ? String(p.precio) : '0',
    rating: p.rating ? String(p.rating) : '5.0',
    img: p.img || (galeria.length > 0 ? galeria[0] : 'https://res.cloudinary.com/dkrzgitcl/image/upload/v1771685981/casas-ernestina/fyhqmdx78fhjq3bxvd18.jpg'),
    galeria: galeria.length > 0 ? galeria : (p.img ? [p.img] : []),
    ubicacion: p.ubicacion || 'Oaxaca, México',
    mapa_embed: p.mapa_embed || '',
    descripcion: p.descripcion || '',
    huespedes: parseInt(p.huespedes, 10) || 1,
    dormitorios: parseInt(p.dormitorios, 10) || 1,
    banios: parseInt(p.banios, 10) || 1,
    amenidades,
    created_at: p.created_at || new Date().toISOString(),
    updated_at: p.updated_at || new Date().toISOString()
  };
}

/**
 * Consulta directa a Turso Cloud vía HTTP Pipeline.
 */
async function fetchFromTurso() {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4000);

    const response = await fetch(TURSO_HTTP_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${TURSO_AUTH_TOKEN}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        requests: [
          { type: 'execute', stmt: { sql: 'SELECT * FROM propiedades ORDER BY id ASC' } },
          { type: 'close' }
        ]
      }),
      signal: controller.signal
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Turso HTTP status: ${response.status}`);
    }

    const data = await response.json();
    const result = data?.results?.[0]?.response?.result;
    if (!result || !result.rows || result.rows.length === 0) {
      return null;
    }

    const cols = result.cols.map(c => c.name);
    const rows = result.rows.map(row => {
      const obj = {};
      cols.forEach((colName, idx) => {
        obj[colName] = row[idx]?.value;
      });
      return obj;
    });

    return rows;
  } catch (err) {
    console.warn('⚠️ Consulta a Turso DB falló:', err.message);
    return null;
  }
}

/**
 * Consulta al Backend Express / Render API.
 */
async function fetchFromApi() {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2500);

    const res = await fetch(`${API_URL}/propiedades`, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        return data;
      }
    }
  } catch (err) {
    console.warn(`⚠️ API Backend (${API_URL}) no disponible:`, err.message);
  }
  return null;
}

/**
 * Consulta a Supabase (como fallback opcional).
 */
async function fetchFromSupabase() {
  try {
    const promise = supabase.from('propiedades').select('*');
    const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Supabase timeout')), 2500));
    const { data, error } = await Promise.race([promise, timeout]);
    if (!error && Array.isArray(data) && data.length > 0) {
      return data;
    }
  } catch (err) {
    console.warn('⚠️ Supabase fallback no respondió:', err.message);
  }
  return null;
}

/**
 * Obtiene las propiedades con estrategia de máxima disponibilidad (Múltiples capas de fallback).
 */
export async function getPropiedades() {
  // Capa 1: Intentar API Backend (Local o Producción)
  let items = await fetchFromApi();

  // Capa 2: Si API falla, consultar Turso Cloud Database directamente
  if (!items || items.length === 0) {
    items = await fetchFromTurso();
  }

  // Capa 3: Si Turso falla, intentar Supabase
  if (!items || items.length === 0) {
    items = await fetchFromSupabase();
  }

  // Capa 4: Si todas las redes fallan, verificar caché de localStorage
  if ((!items || items.length === 0) && isBrowser) {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsedCached = JSON.parse(cached);
        if (Array.isArray(parsedCached) && parsedCached.length > 0) {
          items = parsedCached;
        }
      }
    } catch {
      // Ignorar errores de caché
    }
  }

  // Capa 5: Respaldo final infalible (Dataset por defecto)
  if (!items || items.length === 0) {
    console.info('ℹ️ Usando dataset de respaldo por defecto para propiedades.');
    items = DEFAULT_PROPERTIES;
  }

  // Sanitizar y ordenar propiedades
  const sanitized = items.map(sanitizeProperty).filter(Boolean);

  // Guardar en caché si obtuvimos propiedades válidas
  if (isBrowser && sanitized.length > 0) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(sanitized));
    } catch {
      // Ignorar quota exceeded
    }
  }

  return sanitized;
}

/**
 * Obtener propiedades cacheadas inmediatamente de forma sincrónica para SSR/render inicial rápido.
 */
export function getCachedPropiedades() {
  if (isBrowser) {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map(sanitizeProperty).filter(Boolean);
        }
      }
    } catch {
      // Ignorar error
    }
  }
  return DEFAULT_PROPERTIES.map(sanitizeProperty);
}
